import type { InjectionKey } from 'vue'
import type { SiteEditorAdapters } from '../types/site'

/**
 * Seam between the layer's editor forms and the consuming app.
 *
 * The app passes its adapters as a prop to `<SiteEditorBlockSettingsPanel>`
 * (explicit at the package boundary), which provides them here so the nested
 * per-type settings forms can reach them without prop threading.
 */
export const SiteEditorAdaptersKey: InjectionKey<SiteEditorAdapters> = Symbol('site-editor-adapters')

/**
 * Reads the adapters inside a settings form.
 *
 * Falls back to a no-op uploader so a form rendered outside the panel (or by
 * an app that has not wired uploads yet) degrades to "paste a URL" instead of
 * throwing during render.
 */
export function useSiteEditorAdapters(): SiteEditorAdapters {
  return inject(SiteEditorAdaptersKey, {
    upload: () => Promise.reject(new Error('No upload adapter provided to the site editor.'))
  })
}

/**
 * The settings-form translator, with the layer's own no-op fallback.
 *
 * Every form calls `t(key, fallback)` unconditionally rather than checking
 * for an adapter first — this is what makes that safe. An app with no `t`
 * (or a key it hasn't localised yet) sees exactly the English fallback,
 * which is the current, un-migrated behaviour.
 */
export function useSiteEditorT(): (key: string, fallback: string) => string {
  const { t } = useSiteEditorAdapters()
  return t ?? ((_key: string, fallback: string) => fallback)
}
